import cloneDeep from 'lodash/cloneDeep';
import { getAll as getAllParametre } from '@/api/parametre/index';
import { LIST_PARAMETER_WITH_SOCIETE_ID } from '@/configuration/parametrage/parameterConf';
import { getAll as getAllUtilisateur } from '@/api/utilisateur/index';
import { get as getParametreInterne } from '@/api/settings';
import store from '@/store/store';
import nomPrenomGetter from '@/services/general/nomPrenomGetter';
import * as allTypes from '../mutation-type';
import * as types from './parametres-mutation-type';

function initParametres() {
  const parametres = {};
  Object.keys(types.PARAMETRES_KEYS).forEach(key => {
    const url = types.PARAMETRES_KEYS[key];
    parametres[url] = { items: [], lastUpdate: 0 };
  });
  return parametres;
}

function initParametresInterne() {
  const parametresInterne = {};
  Object.keys(types.PARAMETRES_INTERNE_KEYS).forEach(key => {
    const url = types.PARAMETRES_INTERNE_KEYS[key].key;
    parametresInterne[url] = { items: [], lastUpdate: 0 };
  });
  return parametresInterne;
}

function initialState() {
  const parametres = initParametres();
  const parametresInterne = initParametresInterne();
  return {
    tarifsUnitaire: [],
    tarifsLibre: [],
    parametres,
    parametresInterne,
  };
}

const state = initialState();

const namespaced = true;

const mutations = {
  [types.SET_TARIFS_UNITAIRE](state, tarifs) {
    state.tarifsUnitaire = tarifs;
  },
  [types.SET_TARIFS_LIBRE](state, tarifs) {
    state.tarifsLibre = tarifs;
  },
  [types.SET_PARAMETRE](state, objetItemsParametre) {
    state.parametres[objetItemsParametre.type] = {
      items: objetItemsParametre.items,
      lastUpdate: new Date(),
    };
  },
  [types.SET_PARAMETRE_INTERNE](state, objetItemsParametre) {
    state.parametresInterne[objetItemsParametre.type] = {
      items: objetItemsParametre.items,
      lastUpdate: new Date(),
    };
  },
  [types.RESET_PARAMETRES](state) {
    state.parametres = initParametres();
  },
  [types.RESET_PARAMETRES_INTERNE](state) {
    state.parametresInterne = initParametresInterne();
  },
  [allTypes.RESET_STATE](state) {
    const s = initialState();
    Object.keys(state).forEach(key => {
      state[key] = s[key];
    });
  },
};

const actions = {
  updateUtilisateurToParametre({ state, commit }, user) {
    const type = 'commun/Utilisateur';
    const { items } = state.parametres[type];
    const users = cloneDeep(items);
    const index = users.findIndex(u => u.id === user.id);
    if (index === -1) return;
    users.splice(index, 1, {
      ...user,
      decorator: { nomPrenom: nomPrenomGetter(user) },
    });
    commit(types.SET_PARAMETRE, { type, items: users });
  },
  async loadParametreUtilisateur({ state, dispatch }) {
    const param = state.parametres['commun/Utilisateur'];
    let dateLimit = new Date();
    dateLimit = dateLimit.setHours(dateLimit.getHours() - 24);
    if (param
      && param.lastUpdate
      && param.lastUpdate > dateLimit) {
      return;
    }
    await dispatch('loadParametreUtilisateurForce', 'commun/Utilisateur');
  },
  async loadParametreUtilisateurForce({ commit }, type) {
    if (!type) {
      return;
    }
    const utilisateurs = (await getAllUtilisateur()).data.result;
    utilisateurs.forEach((utilisateur) => {
      utilisateur.decorator = {
        nomPrenom: nomPrenomGetter(utilisateur),
      };
    });
    commit(types.SET_PARAMETRE, { type: type, items: utilisateurs });
  },

  async loadParametres({ state, dispatch }, types) {
    if (!types) {
      return;
    }
    const typesLoadTask = [];
    types.forEach((type) => {
      if (!type) {
        return;
      }
      const param = state.parametres[type];
      let dateLimit = new Date();
      dateLimit = dateLimit.setHours(dateLimit.getHours() - 24);

      if (param
        && param.lastUpdate
        && param.lastUpdate > dateLimit) {
        return;
      }
      typesLoadTask.push(dispatch('loadParametreForce', type));
    });
    await Promise.all(typesLoadTask);
  },
  async loadParametreForce({ commit }, type) {
    if (!type) {
      return;
    }
    const itemsResponse = await getAllParametre(type);
    commit(types.SET_PARAMETRE, { type: type, items: itemsResponse.data.result.items });
  },
  async loadParametresInterne({ state, dispatch }, types) {
    if (!types) return;
    const typesLoadTask = [];
    types.forEach((type) => {
      if (!type) return;
      const param = state.parametresInterne[type];
      let dateLimit = new Date();
      dateLimit = dateLimit.setHours(dateLimit.getHours() - 24);
      if (param
        && param.lastUpdate
        && param.lastUpdate > dateLimit) return;
      typesLoadTask.push(dispatch('loadParametreInterneForce', type));
    });
    await Promise.all(typesLoadTask);
  },
  async loadParametreInterneForce({ commit }, type) {
    if (!type) return;
    const itemResponse = (await getParametreInterne(type)).data.result;
    commit(types.SET_PARAMETRE_INTERNE, { type: type, items: itemResponse });
  },
  purgerParametres({ commit }) {
    commit(types.RESET_PARAMETRES);
    commit(types.RESET_PARAMETRES_INTERNE);
  },
};

const getters = {
  tarifsUnitaire: state => {
    return state.tarifsUnitaire;
  },
  tarifsLibre: state => {
    return state.tarifsLibre;
  },
  parametresInterne: (state) => (ref) => {
    if (!ref || !state.parametresInterne[ref]) return null;
    if (Object.keys(types.PARAMETRES_INTERNE_KEYS).find(key => {
      return types.PARAMETRES_INTERNE_KEYS[key].type === Boolean
          && types.PARAMETRES_INTERNE_KEYS[key].key === ref;
    })) return state.parametresInterne[ref].items === 'true';
    return state.parametresInterne[ref].items;
  },
  parametre: (state) => (ref) => {
    if (!ref || !state.parametres[ref]) {
      return null;
    }
    const isParameterWithSocieteId = LIST_PARAMETER_WITH_SOCIETE_ID.includes(ref);
    if (isParameterWithSocieteId) {
      const societeId = store.getters['societe/societeActiveId'];
      const parametres = state.parametres[ref].items.filter((parametre) => {
        return parametre.societeId === societeId;
      });
      return parametres;
    }
    return state.parametres[ref].items;
  },
  parametresWithoutFilterSocieteId: (state) => (ref) => {
    if (!ref || !state.parametres[ref]) {
      return null;
    }
    return state.parametres[ref].items;
  },
};

export default {
  namespaced,
  state,
  mutations,
  actions,
  getters,
};
